import { useState } from 'react';

interface ServiceItem {
  name: string;
  price: string;
  duration: string;
}

interface Service {
  id: number;
  category: string;
  items: ServiceItem[];
  icon: string;
}

interface ServicesTabProps {
  data: Service[];
  add: (item: Omit<Service, 'id'> & { id?: number | string }) => Promise<void>;
  update: (id: number, updates: Partial<Service>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export default function ServicesTab({
  data,
  add,
  update,
  remove,
}: ServicesTabProps) {
  const [editing, setEditing] = useState<Service | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-brand-black text-xl">
          Servicios de Arreglos
        </h3>
        <button
          onClick={() => {
            setIsAdding(true);
            setEditing(null);
          }}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Nuevo servicio
        </button>
      </div>

      {(isAdding || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const payload: Omit<Service, 'id'> = {
              category: fd.get('category') as string,
              icon: fd.get('icon') as string,
              items: (fd.get('items') as string)
                .split('\n')
                .filter(Boolean)
                .map((line) => {
                  const [name, price, duration] = line
                    .split('|')
                    .map((s) => s.trim());
                  return {
                    name: name || '',
                    price: price || '',
                    duration: duration || '',
                  };
                }),
            };
            if (editing) {
              update(editing.id, payload);
              setEditing(null);
            } else {
              add(payload);
              setIsAdding(false);
            }
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h4 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Servicio' : 'Nuevo Servicio'}
            </h4>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Categoría *
              </label>
              <input
                name="category"
                defaultValue={editing?.category ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Icono (clase Remix)
              </label>
              <input
                name="icon"
                defaultValue={editing?.icon ?? 'ri-scissors-cut-line'}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Items (nombre | precio | duración, uno por línea)
            </label>
            <textarea
              name="items"
              defaultValue={
                editing?.items
                  .map((i) => `${i.name} | ${i.price} | ${i.duration}`)
                  .join('\n') ?? ''
              }
              rows={5}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((s) => (
          <div
            key={s.id}
            className="bg-white border border-brand-gray-200 rounded-2xl p-5 hover:border-brand-gray-300 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black">
                  <i className={`${s.icon} text-lg`} />
                </div>
                <h4 className="font-sans text-brand-black text-sm font-medium">
                  {s.category}
                </h4>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setEditing(s);
                    setIsAdding(false);
                  }}
                  className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                >
                  <i className="ri-pencil-line text-xs" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar "${s.category}"?`))
                      remove(s.id);
                  }}
                  className="w-7 h-7 rounded-md bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              </div>
            </div>
            <ul className="space-y-2">
              {s.items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between font-sans text-sm"
                >
                  <span className="text-brand-gray-600">{item.name}</span>
                  <span className="text-brand-black font-medium">
                    {item.price}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}